/** Mirrors the backend's `GeoReferenceRejectionReason` symbolic strings (Principle V). */
export type GeoImageRejectionReason = 'UnsupportedFormat' | 'NoGeoreferencing' | 'UnreadableFile';

/** Mirrors the backend's `UploadedGeoImageKind` symbolic strings. */
export type GeoImageKind = 'Photo' | 'Raster';

/** One file staged on the preview screen (FR-007), before "Start processing" is pressed. */
export interface GeoImageCard {
  file: File;
  previewUrl: string;
  title: string;
  /** Detected or manually entered — a `Photo` needs this before it can be checked (FR-004). */
  coordinates: { latitude: number; longitude: number } | null;
  /** WKT footprint, set only for an accepted `Raster` (research R-009 / contract `Inspect`). */
  footprintWkt: string | null;
  captureDate: Date | null;
  tagIds: string[];
  imageKind: GeoImageKind | null;
  /** `null` while still being checked, `false` once accepted (client EXIF or server Inspect). */
  rejectionReason: GeoImageRejectionReason | null;
  /** True while EXIF is being read client-side or, for a GeoTIFF, while the Inspect call is in flight. */
  isChecking: boolean;
  /** User's checkbox state — only checked cards are sent on "Start processing" (FR-008). */
  selected: boolean;
  errors: string[];
}

export function isGeoTiffFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.tif') || name.endsWith('.tiff');
}

export function isPhotoFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
}

/** Client-side pre-filter mirroring the server's extension allow-list (FR-002/FR-003) — the server
 * re-validates independently regardless (research R-004), this only avoids a pointless round trip. */
export function isAcceptableGeoImageFile(file: File): boolean {
  return isPhotoFile(file) || isGeoTiffFile(file);
}
